import { ApplicationInsights } from "@microsoft/applicationinsights-web"

let client: ApplicationInsights | null = null

// Matches a UUID anywhere — path segment or query-string value — bounded by
// non-hex/dash so it can't partial-match inside a longer token.
const UUID_RE =
  /(?<![0-9a-f-])[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?![0-9a-f-])/gi

export function redactUrl(url: string): string {
  return url.replace(UUID_RE, "[redacted]")
}

/**
 * Which app is reporting, as `cloud_RoleName`.
 *
 * Required rather than defaulted: both front ends send to one App Insights
 * resource, and without it their browser telemetry is one undifferentiated pile.
 */
export type AppRole = "feedback-app" | "practitioner-portal"

export function initTelemetry(role: AppRole): void {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING
  if (!connectionString) return

  client = new ApplicationInsights({
    config: {
      connectionString,
      samplingPercentage: Number(
        import.meta.env.VITE_APPINSIGHTS_SAMPLING ?? 100,
      ),
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: false,
      // Chrome is retiring `unload` and blocks the listener, which the SDK
      // registers to flush on page exit — the console reports it as a
      // permissions-policy violation even though we send no such header.
      // Naming it here leaves the SDK on `pagehide` and `visibilitychange`,
      // which it hooks anyway and which fire when a mobile browser backgrounds
      // the page, where `unload` never did.
      disablePageUnloadEvents: ["unload"],
      // The SDK's own usage stats go to a Microsoft endpoint, not our resource,
      // and to a host the CSP doesn't allow. 2 is FeatureOptInMode.disable — an
      // ambient const enum, which verbatimModuleSyntax can't import.
      featureOptIn: { sdkStats: { mode: 2 } },
    },
  })

  client.addTelemetryInitializer((item) => {
    item.tags = item.tags ?? {}
    item.tags["ai.cloud.role"] = role
    item.tags["ai.application.ver"] = import.meta.env.VITE_APP_VERSION ?? "dev"
    item.data = item.data ?? {}
    item.data["environment"] = import.meta.env.VITE_APP_ENV ?? "local"
  })

  client.addTelemetryInitializer((item) => {
    if (!item.baseData) return
    for (const field of ["uri", "url", "data", "name"] as const) {
      if (typeof item.baseData[field] === "string") {
        item.baseData[field] = redactUrl(item.baseData[field] as string)
      }
    }
  })

  client.loadAppInsights()
}

export function trackException(
  error: Error,
  properties?: Record<string, string>,
): void {
  client?.trackException({ exception: error, properties })
}

export function trackError(
  error: unknown,
  properties?: Record<string, string>,
): void {
  trackException(
    error instanceof Error ? error : new Error(String(error)),
    properties,
  )
}

export function flushTelemetry(): void {
  void client?.flush()
}
